import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = [
  { emoji: '😌', label: 'Apaisé(e)' },
  { emoji: '😊', label: 'Content(e)' },
  { emoji: '🤔', label: 'Pensif(ve)' },
  { emoji: '💪', label: 'Motivé(e)' },
  { emoji: '😴', label: 'Détendu(e)' },
  { emoji: '🙂', label: 'Neutre' },
];

export const JournalPrompt = ({ themeSlug, mode, themeTitle, onClose, onSkip }) => {
  const { token } = useAuth();
  const [selectedMood, setSelectedMood] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!selectedMood) {
      toast.error('Sélectionnez un ressenti');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/journal/entry`,
        {
          theme_slug: themeSlug,
          mode: mode,
          mood: selectedMood,
          note: note.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ressenti enregistré !');
      onClose();
    } catch (error) {
      console.error('Erreur journal:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div 
      className="fixed inset-0 z-[9999] flex items-end justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div 
        className="bg-white w-full max-w-lg rounded-t-3xl p-6 pb-10 animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-sage-700">Comment vous sentez-vous ?</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onSkip}
            className="text-sage-500 hover:text-sage-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <p className="text-sm text-sage-500 mb-4">
          Après <span className="text-sage-700 font-medium">{themeTitle}</span> - {mode === 'zen' ? 'Mode Zen' : 'Mode Mouvement'}
        </p>

        {/* Mood selection */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {MOODS.map((mood) => (
            <button
              key={mood.emoji}
              onClick={() => setSelectedMood(mood.emoji)}
              className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                selectedMood === mood.emoji
                  ? 'border-declic bg-declic/5 scale-105'
                  : 'border-sand-200 hover:border-sage-300'
              }`}
            >
              <span className="text-3xl mb-1">{mood.emoji}</span>
              <span className="text-xs text-sage-600">{mood.label}</span>
            </button>
          ))}
        </div>

        {/* Optional note */}
        <div className="mb-5">
          <label className="text-sm text-sage-600 mb-2 block">
            Une pensée ? (optionnel)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 200))}
            placeholder="En quelques mots..."
            className="w-full p-3 border border-sand-200 rounded-xl text-sage-700 text-sm resize-none focus:outline-none focus:border-sage-400"
            rows={2}
            maxLength={200}
          />
          <p className="text-xs text-sage-400 text-right mt-1">{note.length}/200</p>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={onSkip}
            className="flex-1 h-12 border-sage-300 text-sage-600"
          >
            Passer
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !selectedMood}
            className="flex-1 h-12 bg-sage-500 hover:bg-sage-600 text-white"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default JournalPrompt;
